import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useConfig } from "@/context/ConfigContext";
import { assetUrl } from "@/lib/api";
import { track } from "@/lib/analytics";

export const Brand = ({ className = "" }) => {
  const { config } = useConfig();
  const name = config?.platform_name || "Book Rosa Brasil";
  if (config?.logo_url) {
    return <img src={assetUrl(config.logo_url)} alt={name} className={`h-9 w-auto ${className}`} />;
  }
  return (
    <span className={`inline-flex items-center gap-2.5 font-display text-xl tracking-tight text-white ${className}`} data-testid="brand-mark">
      <img src="/images/brand-mark.png" alt="" aria-hidden="true" className="h-9 w-auto shrink-0" />
      <span>{name.split(" ").slice(0, -1).join(" ")} <em className="italic text-pink-400">{name.split(" ").slice(-1)}</em></span>
    </span>
  );
};

const NAV = [
  { to: "/", label: "Início", id: "home" },
  { to: "/inscricao", label: "Inscrição", id: "apply" },
  { to: "/contato", label: "Contato", id: "contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
    track("page_view");
  }, [pathname]);
  return (
    <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#0B090C]/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="Página inicial"><Brand /></Link>
        <nav className="hidden items-center gap-8 md:flex" aria-label="Principal">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.id}-link`}
              className={({ isActive }) => `text-sm transition-colors duration-200 ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`}>
              {n.label}
            </NavLink>
          ))}
          <Link to="/inscricao" onClick={() => track("cta_click", { label: "header" })} className="btn-primary !px-5 !py-2.5" data-testid="header-apply-cta">Quero me inscrever</Link>
        </nav>
        <button className="rounded-md p-2 text-zinc-200 md:hidden" onClick={() => setOpen(!open)} aria-expanded={open} aria-label="Abrir menu" data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <nav className="border-t border-white/[0.06] bg-[#0B090C] px-5 py-4 md:hidden" aria-label="Menu móvel" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="block py-3 text-base text-zinc-200" data-testid={`mobile-nav-${n.id}-link`}>{n.label}</Link>
          ))}
        </nav>
      )}
    </header>
  );
};

const Footer = () => {
  const { config } = useConfig();
  return (
    <footer className="border-t border-white/[0.06] bg-[#08070A]" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 sm:px-8 md:grid-cols-12">
        <div className="md:col-span-5">
          <Brand />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-zinc-400">{config?.institutional_info}</p>
          {config?.controller_name && <p className="mt-3 text-xs text-zinc-500">Responsável pelo tratamento: {config.controller_name}</p>}
        </div>
        <div className="md:col-span-3">
          <p className="eyebrow mb-4">Institucional</p>
          <ul className="space-y-3 text-sm text-zinc-300">
            <li><Link to="/privacidade" className="hover:text-white" data-testid="footer-privacy-link">Política de privacidade</Link></li>
            <li><Link to="/termos" className="hover:text-white" data-testid="footer-terms-link">Termos de uso</Link></li>
            <li><Link to="/contato" className="hover:text-white" data-testid="footer-contact-link">Contato</Link></li>
            <li><Link to="/admin/login" className="text-zinc-400 hover:text-white" data-testid="footer-admin-link">Área administrativa</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <p className="eyebrow mb-4">Proteção de dados</p>
          <p className="flex gap-2 text-sm leading-relaxed text-zinc-400">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-pink-400" aria-hidden />
            Dados e fotografias são privados, usados apenas para avaliar candidaturas e acessíveis somente à equipe autorizada (LGPD — Lei 13.709/2018).
          </p>
          {config?.dpo_email && (
            <p className="mt-3 text-sm text-zinc-400">Solicitações do titular: <a className="text-pink-300 underline-offset-4 hover:underline" href={`mailto:${config.dpo_email}`}>{config.dpo_email}</a></p>
          )}
        </div>
      </div>
      <div className="border-t border-white/[0.05] px-5 py-6 text-center text-xs text-zinc-500">
        © {new Date().getFullYear()} {config?.platform_name}. Inscrições exclusivas para maiores de 18 anos.
      </div>
    </footer>
  );
};

export const PublicLayout = () => (
  <div className="flex min-h-screen flex-col">
    <a href="#conteudo" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-pink-600 focus:px-4 focus:py-2">Pular para o conteúdo</a>
    <Header />
    <main id="conteudo" className="flex-1"><Outlet /></main>
    <Footer />
  </div>
);
